/**
 * Legal placeholders — Terms of Service and Privacy Policy.
 *
 * Stubbed for now to prevent dead links from the signup form. Replace each
 * <Body /> block with real legal copy before launch; the route surface stays
 * the same.
 */

import { FileText, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { useSeo } from '@/hooks/useSeo';

function Frame({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof FileText;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-hairline px-6 py-4">
        <Link
          to="/"
          className="text-sm font-semibold tracking-tight text-foreground"
        >
          <span className="text-primary">ai</span>costr
        </Link>
      </header>
      <main className="mx-auto max-w-3xl px-6 py-12">
        <Card className="border-hairline">
          <CardContent className="space-y-6 px-8 py-10">
            <div className="flex items-center gap-3">
              <span className="grid size-10 place-items-center rounded-lg bg-muted text-muted-foreground">
                <Icon className="size-5" />
              </span>
              <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
            </div>
            <div className="prose prose-sm max-w-none text-sm text-muted-foreground [&_h2]:mt-6 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-foreground">
              {children}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

export function Terms() {
  useSeo({ title: 'Terms of Service · aicostr', canonicalPath: '/terms' });
  return (
    <Frame icon={FileText} title="Terms of Service">
      <p>
        These Terms govern your use of Aicostr. By creating an account
        you accept them. This is a placeholder until the production document
        is published.
      </p>
      <h2>1. Service</h2>
      <p>
        Aicostr provides cost tracking, anomaly detection, and budget
        controls for AI API usage. The service is provided as-is during the
        current beta period.
      </p>
      <h2>2. Account</h2>
      <p>
        You are responsible for credentials and any usage incurred under your
        organisation. Workspace owners may invite, remove, and audit team
        members.
      </p>
      <h2>3. Data</h2>
      <p>
        We process the metadata you send to our /track endpoint (provider,
        model, tokens, latency, optional feature tag) for the purposes of
        cost calculation and anomaly detection. We do not store prompt or
        completion content unless you explicitly enable that capability.
      </p>
      <p className="mt-8 text-xs">
        Contact: legal@aicostr.com — replace before launch.
      </p>
    </Frame>
  );
}

export function Privacy() {
  useSeo({ title: 'Privacy Policy · aicostr', canonicalPath: '/privacy' });
  return (
    <Frame icon={ShieldCheck} title="Privacy Policy">
      <p>
        This Privacy Policy describes how Aicostr collects, uses, and
        shares information. This is a placeholder until the production
        document is published.
      </p>
      <h2>Data we collect</h2>
      <ul>
        <li>Account identity (email, display name) from Firebase Auth.</li>
        <li>Workspace metadata (organisation name, members, roles).</li>
        <li>Usage events (provider, model, tokens, cost, latency, feature).</li>
        <li>Audit log entries for sensitive workspace actions.</li>
      </ul>
      <h2>Data we do NOT collect by default</h2>
      <ul>
        <li>Prompt or completion contents.</li>
        <li>Customer-of-customer PII unless you tag it explicitly.</li>
      </ul>
      <h2>Sub-processors</h2>
      <p>
        Google Firebase (auth + Firestore + Cloud Functions hosting). Slack
        (optional, only when you configure a webhook for alerts).
      </p>
      <p className="mt-8 text-xs">
        Contact: privacy@aicostr.com — replace before launch.
      </p>
    </Frame>
  );
}
